import React, { useEffect, useState } from 'react';
import { Building2, Loader2, MapPin, Phone, RefreshCw, X } from 'lucide-react';

import estabelecimentoService, {
  type Estabelecimento,
} from '../../services/estabelecimentoService';
import clienteHospitalLocalService, {
  type LocalVinculado,
} from '../../services/cliente/clienteHospitalLocalService';

interface HospitalConsultaModalProps {
  hospitalId: number;
  clienteId: number;
  onClose: () => void;
}

function valueOrDash(value?: string | null): string {
  const normalized = value?.trim();
  return normalized || 'Não informado';
}

export default function HospitalConsultaModal({
  hospitalId,
  clienteId,
  onClose,
}: HospitalConsultaModalProps) {
  const [hospital, setHospital] = useState<Estabelecimento | null>(null);
  const [locais, setLocais] = useState<LocalVinculado[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [hospitalData, locaisData] = await Promise.all([
        estabelecimentoService.getEstabelecimento(hospitalId),
        clienteHospitalLocalService.getLocaisVinculados(clienteId, hospitalId),
      ]);
      setHospital(hospitalData);
      setLocais(locaisData);
    } catch {
      setError('Não foi possível carregar os dados deste hospital.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [hospitalId, clienteId]);

  const address = hospital
    ? [
        hospital.endereco,
        hospital.bairro,
        hospital.cidade,
        hospital.estado,
        hospital.cep && `CEP ${estabelecimentoService.formatCEP(hospital.cep)}`,
      ]
        .filter(Boolean)
        .join(' - ')
    : '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hospital-consulta-title"
    >
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div className="flex min-w-0 items-center gap-3">
            <Building2 className="h-5 w-5 shrink-0 text-blue-600" />
            <div className="min-w-0">
              <h2
                id="hospital-consulta-title"
                className="truncate text-lg font-semibold text-gray-900 dark:text-white"
              >
                Dados do hospital
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Cadastro e pontos vinculados ao cliente deste agendamento
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white"
            title="Fechar"
            aria-label="Fechar consulta do hospital"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-16 text-gray-600 dark:text-gray-300">
              <Loader2 className="h-5 w-5 animate-spin" />
              Carregando dados...
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              <button
                type="button"
                onClick={load}
                className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-900 dark:text-blue-300"
              >
                <RefreshCw className="h-4 w-4" />
                Tentar novamente
              </button>
            </div>
          )}

          {!loading && hospital && !error && (
            <>
              <section className="border-b border-gray-200 pb-5 dark:border-gray-700">
                <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-xs font-medium uppercase text-gray-500">Razão social</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                      {valueOrDash(hospital.razao_social)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase text-gray-500">Nome fantasia</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                      {valueOrDash(hospital.nome_fantasia)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase text-gray-500">CNPJ/CPF</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                      {hospital.cnpj
                        ? estabelecimentoService.formatCNPJ(hospital.cnpj)
                        : 'Não informado'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase text-gray-500">Código</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                      {valueOrDash(hospital.codigo)}
                    </dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="flex items-center gap-1 text-xs font-medium uppercase text-gray-500">
                      <MapPin className="h-3.5 w-3.5" />
                      Endereço
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                      {valueOrDash(address)}
                    </dd>
                  </div>
                  <div>
                    <dt className="flex items-center gap-1 text-xs font-medium uppercase text-gray-500">
                      <Phone className="h-3.5 w-3.5" />
                      Telefone
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                      {hospital.telefone
                        ? estabelecimentoService.formatTelefone(hospital.telefone)
                        : 'Não informado'}
                      {hospital.ramal ? ` ramal ${hospital.ramal}` : ''}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase text-gray-500">E-mail</dt>
                    <dd className="mt-1 break-all text-sm text-gray-900 dark:text-white">
                      {valueOrDash(hospital.email)}
                    </dd>
                  </div>
                </dl>
              </section>

              <section className="pt-5">
                <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                  Pontos de contagem vinculados
                </h3>
                {locais.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Nenhum ponto de contagem vinculado a este cliente.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-gray-300 text-left dark:border-gray-600">
                          <th className="px-2 py-2 font-medium">Local</th>
                          <th className="px-2 py-2 font-medium">Responsável</th>
                          <th className="px-2 py-2 font-medium">Telefone</th>
                          <th className="px-2 py-2 font-medium">E-mail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {locais.map(local => (
                          <tr
                            key={local.id}
                            className="border-b border-gray-200 dark:border-gray-700"
                          >
                            <td className="px-2 py-2">{valueOrDash(local.local_nome)}</td>
                            <td className="px-2 py-2">{valueOrDash(local.responsavel)}</td>
                            <td className="px-2 py-2">
                              {valueOrDash(local.telefone)}
                              {local.ramal ? ` ramal ${local.ramal}` : ''}
                            </td>
                            <td className="break-all px-2 py-2">{valueOrDash(local.email)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
